import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '../UI/Card';
import Button from '../UI/Button';
import BankFormModal from './BankFormModal';
import bankService, { BankItem } from '../../api/services/bank.service';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingLibraryIcon,
  WalletIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

const BanksTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<BankItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<BankItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const {
    data: banks,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['banks'],
    queryFn: () => bankService.getAll(),
    staleTime: 60 * 1000,
  });

  const list = banks || [];

  const handleAdd = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const handleEdit = (bank: BankItem) => {
    setEditing(bank);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['banks'] });
  };

  const handleToggleActive = async (bank: BankItem) => {
    setBusyId(bank._id);
    try {
      await bankService.toggleActive(bank._id);
      queryClient.invalidateQueries({ queryKey: ['banks'] });
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleWithdraw = async (bank: BankItem) => {
    setBusyId(bank._id);
    try {
      await bankService.update(bank._id, {
        withdrawEnabled: !bank.withdrawEnabled,
      });
      queryClient.invalidateQueries({ queryKey: ['banks'] });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (bank: BankItem) => {
    setBusyId(bank._id);
    try {
      await bankService.remove(bank._id);
      queryClient.invalidateQueries({ queryKey: ['banks'] });
      setDeleteConfirm(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Payment Accounts
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Banks and wallets users can deposit into.
            </p>
          </div>
          <Button size="sm" onClick={handleAdd}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Bank
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error
                ? error.message
                : 'Failed to load banks'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-10">
            <BuildingLibraryIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No banks configured yet</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Add your first payment account.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {list.map((b) => (
              <div
                key={b._id}
                className={`border rounded-lg p-3 transition-colors ${
                  b.isActive
                    ? 'border-gray-100 hover:border-primary-200'
                    : 'border-gray-100 bg-gray-50/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        b.kind === 'wallet'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-primary-50 text-primary-600'
                      }`}
                    >
                      {b.kind === 'wallet' ? (
                        <WalletIcon className="w-4 h-4" />
                      ) : (
                        <BuildingLibraryIcon className="w-4 h-4" />
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {b.bankName}
                        </p>
                        <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase bg-gray-100 text-gray-600">
                          {b.shortCode}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                        {b.accountName} · {b.accountNumber}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {b.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        Disabled
                      </span>
                    )}

                    {b.withdrawEnabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                        <BanknotesIcon className="w-3 h-3" />
                        Withdrawal
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
                        No Withdrawal
                      </span>
                    )}
                  </div>
                </div>

                {b.instructions.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-2 truncate">
                    {b.instructions.length} instruction step
                    {b.instructions.length !== 1 ? 's' : ''}
                  </p>
                )}

                <div className="flex items-center justify-end gap-1.5 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleToggleWithdraw(b)}
                    disabled={busyId === b._id}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors disabled:opacity-50 ${
                      b.withdrawEnabled
                        ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                    title={
                      b.withdrawEnabled
                        ? 'Disable withdrawal for this bank'
                        : 'Enable withdrawal for this bank'
                    }
                  >
                    {busyId === b._id
                      ? '...'
                      : b.withdrawEnabled
                      ? 'Disable Withdrawal'
                      : 'Enable Withdrawal'}
                  </button>

                  <button
                    onClick={() => handleToggleActive(b)}
                    disabled={busyId === b._id}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors disabled:opacity-50 ${
                      b.isActive
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    {busyId === b._id
                      ? '...'
                      : b.isActive
                      ? 'Disable'
                      : 'Enable'}
                  </button>

                  <button
                    onClick={() => handleEdit(b)}
                    className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                    title="Edit"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(b)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create / Edit modal */}
      <BankFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        bank={editing}
      />

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Delete {deleteConfirm.bankName}?
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              This cannot be undone. Users will no longer see this bank as a
              deposit option.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                disabled={busyId === deleteConfirm._id}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={busyId === deleteConfirm._id}
              >
                {busyId === deleteConfirm._id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BanksTab;